import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import apiService, { User } from "@/services/api";

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (token: string, userData: User) => void;
  logout: () => Promise<void>;
  loading: boolean;
  isLoggingOut: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    // Check if user is authenticated on mount
    const token = localStorage.getItem("admin-token");
    console.log("AuthContext - Checking token:", token ? "Found" : "Not found");

    const loadUserData = async () => {
      if (token) {
        try {
          // Set the token in the API service
          apiService.setAuthToken(token);
          
          // Fetch actual user data from the API
          const userResponse = await apiService.getCurrentAdminUser();
          
          if (userResponse.type === 0 && userResponse.data) {
            const userData = userResponse.data;
            // Map the response data to User interface
            const user: User = {
              _id: userData._id || userData.id,
              fullName: userData.fullName || userData.name || "Admin User",
              email: userData.email,
              role: userData.role || 3, // Admin role (3 from user_type enum)
              userStatus: userData.userStatus || 2, // Active status
              profilePicture: userData.profilePicture,
              createdAt: userData.createdAt || new Date().toISOString(),
              updatedAt: userData.updatedAt || new Date().toISOString(),
            };
            
            setUser(user);
            setIsAuthenticated(true);
            console.log("AuthContext - User authenticated with actual data:", user);
          } else {
            console.warn("AuthContext - Failed to fetch user data:", userResponse.message);
            // Token exists but user data fetch failed - still set authenticated but with null user
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error("AuthContext - Error fetching user data:", error);
          // Token exists but API call failed - still set authenticated but with null user
          setIsAuthenticated(true);
        }
      } else {
        console.log("AuthContext - No token found, user not authenticated");
        setIsAuthenticated(false);
      }
      setLoading(false);
    };

    loadUserData();
  }, []);

  const login = (token: string, userData: User) => {
    apiService.setAuthToken(token);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      // Call backend logout API to invalidate refresh token
      await apiService.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
      // Continue with logout even if API call fails
    } finally {
      // Clear local state and redirect
      apiService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setIsLoggingOut(false);
      // Redirect to landing page
      window.location.href = "/landing";
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
    isLoggingOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
